import React, { useEffect, useState } from "react";
import useApi from "../../../../Hooks/useApi";
import GroupStackedBarChart from ".";
import { fetchGroupBarChartData, smartSort } from "./Utils";
import EmptyChart from "../EmptyChart";
import LoadingSpinner from "../../../Shared/LoadingSpinner";

const GroupStackBarContainer = ({
  groupBy,
  datasetOne,
  containerClassName,
  exportRef,
  selectedCity,
  selectedYear
}) => {

  const [stackBarData, setStackBarData] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [totalValue, setTotalValue] = useState(0);
  const [error, setError] = useState(null);

  const { request, loading } = useApi({ isPublic: true });

  const question_no_01 = datasetOne?.name;
  const question_no_02 = groupBy?.name;

  useEffect(() => {

    setStackBarData([]);
    setSubcategories([]);
    setTotalValue(0);
    setError(null);

    // 2. Input Validation
    if (!selectedCity?.id || !question_no_01 || !question_no_02) {
      return;
    }

    const fetchData = async () => {
      try {
        const response = await fetchGroupBarChartData(request, {
          location: selectedCity?.id,
          query: question_no_01,
          group: question_no_02,
          year: selectedYear
        });

        if (!response) {
          throw new Error("Received empty response from server.");
        }


        if (!Array.isArray(response.data)) {

          console.warn("GroupBar: response.data is not an array", response);
          setStackBarData([]);
          return;
        }


        const dbData = smartSort(response.data);

        const apiTotal = Number(response.total) || 0;

        const result = [];
        const uniqueChildren = new Set();
        const grouped = {};

        for (const item of dbData) {

          if (!item) continue;

          // Safe String Conversion (Prevents crash if parent/child is number or null)
          const category = item.parent ? String(item.parent).trim() : "N/A";
          let childKey = item.child ? String(item.child).trim() : "N/A";

          // Handle "null" string from backend
          if (childKey.toLowerCase() === "null" || childKey === "") {
            childKey = "N/A";
          }

          // Track unique children
          if (childKey !== "N/A") {
            uniqueChildren.add(childKey);
          }

          // Initialize group object
          if (!grouped[category]) {
            grouped[category] = {};
          }

          // Calculate Percentage Safely
          const value = Number(item.value) || 0;
          const percentage = apiTotal > 0 ? (value / apiTotal) * 100 : 0;

          grouped[category][childKey] = percentage;
        }

        // 6. Formatting for Chart
        for (const [category, values] of Object.entries(grouped)) {
          const lowerCat = String(category).trim().toLowerCase();

          if (lowerCat === "null" || lowerCat === "undefined") continue;

          if (category !== "N/A") {
            result.push({ category, values });
          }
        }

        // Add N/A category at the end if it exists
        if (grouped["N/A"]) {
          result.push({ category: "N/A", values: grouped["N/A"] });
        }

        // Subcategory processing
        let subCats = Array.from(uniqueChildren);

        // Ensure N/A is at the end of legends if it exists
        if (subCats.includes("N/A")) {
          subCats = subCats.filter((v) => v !== "N/A");
          subCats.push("N/A");
        }

        setSubcategories(subCats);
        setTotalValue(apiTotal);
        setStackBarData(result);

      } catch (err) {
        console.error("GroupStackBar Error:", err);
        setError("Unable to load chart data due to a processing error.");
      }
    };

    fetchData();
  }, [selectedCity, question_no_01, question_no_02, request]);



  if (loading) {
    return <LoadingSpinner message="Loading Bar Chart..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col justify-center items-center border border-red-200 bg-red-50 rounded">
        <p className="text-red-500 font-semibold">{error}</p>
      </div>
    );
  }

  if (!stackBarData || stackBarData.length === 0 || totalValue === 0) {
    return <EmptyChart />;
  }

  return (
    <GroupStackedBarChart
      data={stackBarData}
      totalValue={totalValue}
      subcategories={subcategories}
      containerClassName={containerClassName}
      exportRef={exportRef}
      selectedCity={selectedCity}
      groupBy={groupBy}
      datasetOne={datasetOne}
      year={selectedYear}
    />
  );
};

export default GroupStackBarContainer;